import { MessageBar, MessageBarType, Separator } from "@fluentui/react";
import React from "react";
import {
    FormValidationSummary,
    Text,
    Stack,
    PrimaryButton,
    ValidationFailure,
    createWarningFailure,
    createValidationResult,
    Spinner,
    WarningMessage
} from "sprint-common-web";

import { FileRecordStatus } from "../../../../../common/types/FileRecordStatus";
import { RecordType } from "../../../../../common/types/RecordType";

export const FILE_RECORD_VALIDATION_PREFIX = "fileRecord.";

export interface CloudFileQcDoneValidationPanelProps {
    cloudFileRecordType: RecordType;
    fileRecordType?: RecordType;
    fileRecordStatus?: FileRecordStatus;
    cloudFileValidationFailures: ValidationFailure[];
    fileRecordValidationFailures: ValidationFailure[];
    onFileRecordOverride: () => void;
    isValidating: boolean;
}

export const CloudFileQcDoneValidationPanel = ({
    cloudFileRecordType,
    fileRecordType,
    fileRecordStatus,
    cloudFileValidationFailures,
    fileRecordValidationFailures,
    isValidating,
    onFileRecordOverride
}: CloudFileQcDoneValidationPanelProps): JSX.Element => {
    const canOverride = fileRecordType === cloudFileRecordType;

    return (
        <Stack tokens={stackTokens}>
            {isValidating ? (
                <Spinner />
            ) : (
                <>
                    <FormValidationSummary
                        styles={validationSummaryStyles}
                        validationResult={createValidationResult(cloudFileValidationFailures)}
                    />
                    {fileRecordValidationFailures.length > 0 && (
                        <FileRecordValidationSummary
                            validationFailures={fileRecordValidationFailures}
                            canOverride={canOverride}
                            fileRecordStatus={fileRecordStatus}
                            onOverride={onFileRecordOverride}
                        />
                    )}
                </>
            )}
        </Stack>
    );
};

const stackTokens = { padding: "10px 0" };

interface FileRecordValidationSummaryProps {
    validationFailures: ValidationFailure[];
    fileRecordStatus?: FileRecordStatus;
    canOverride: boolean;
    onOverride: () => void;
}

const FileRecordValidationSummary = ({
    validationFailures,
    canOverride,
    fileRecordStatus,
    onOverride
}: FileRecordValidationSummaryProps): JSX.Element => {
    if (fileRecordStatus === FileRecordStatus.QcDone) {
        return <WarningMessage message="Associated file record is already in Qc Done status." />;
    }

    if (!canOverride) {
        return (
            <FormValidationSummary
                styles={validationSummaryStyles}
                validationResult={createValidationResult([getTypeMismatchFailure()])}
            />
        );
    }

    return (
        <>
            <FileRecordValidationLabel />
            <FormValidationSummary
                styles={validationSummaryStyles}
                validationResult={createValidationResult(validationFailures)}
            />
            <Separator />
            <OverrideMessage />
            <Stack horizontal horizontalAlign="end" tokens={{ childrenGap: 10, padding: 10 }}>
                <PrimaryButton
                    onClick={onOverride}
                    text="Override"
                    testId="override-file-record-button"
                    type="loading"
                />
            </Stack>
        </>
    );
};

const overrideMessageContent =
    "You can override all relevant file record fields with the current cloud file. Otherwise, file record will not be changed to Qc Done.";

const OverrideMessage = () => {
    return <MessageBar messageBarType={MessageBarType.warning}>{overrideMessageContent}</MessageBar>;
};

const FileRecordValidationLabel = () => {
    return (
        <Text variant="medium" styles={{ root: { paddingBottom: 10 } }}>
            <b>Associated file record validation:</b>
        </Text>
    );
};

const getTypeMismatchFailure = () =>
    createWarningFailure(
        "Associated file record has different type from cloud file and will not be changed to Qc Done."
    );

const validationSummaryStyles = {
    root: { whiteSpace: "pre-line" }
};
